import * as React from 'react';
import { css } from '@emotion/css';
import { connect } from 'react-redux';
import { theme } from '@branchjs/neo-components';

import { retrieveDiscoAdsCampaigns, saveDiscoAdsCreatorStep, updateCurrentCampaign } from '../../../actions/DiscoAdsActions';

import DiscoAdsCreatorViewComponentWrapper from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorViewComponentWrapper';
import DiscoAdsCreatorCustomDropdown from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorCustomDropdown';
import DiscoAdsCreatorFormSection from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorFormSection';
import GeoTargetMultiSelect from '../DiscoAdsComponents/CreatorComponents/GeoTargetMultiSelect';
import {
  getAppName,
  getCampaignSetup,
  getCampaignsNamesAndObjectives,
  getDateRangeInfo,
} from '../selectors/pageStateSelectors';

import { isBidValid } from '../discoAdsUtils';

import { formatStringToDollar, strToTitleCase } from '../../../../main/support/utils';

import Icons from '../../../../../styles/Icons';

import {
  DA_CAMPAIGN_OBJECTIVE_OPTIONS,
  DA_CREATOR_FORM_SECTION_COPY,
  DA_CREATOR_TOOLTIP_COPY,
  STEPPER_CARD_COMPLETE_STEP,
  DA_CAMPAIGN_SETUP,
  AD_OBJECT_SETTINGS_SAVE_BTN_COPY,
} from '../discoAdsConstants';

import MessageBox from '../../../../common/components/core/MessageBox';

const styles = {
  disabledInput: css({
    backgroundColor: theme.colors.gray.neutral,
    opacity: 0.4,
    outline: 'none',
  }),
  budgetInputField: css({
    maxWidth: '200px',
  }),
  campaignObjectiveDropdown: {
    valueContainer: (providedStyling) => ({
      ...providedStyling,
      height: '35px',
      flexWrap: 'unset',
      position: 'unset',
    }),
  },
};

const campaignSettingsCustomFormWrapper = {
  formContentContainer: {
    display: 'flex',
    flexGrow: 1,
    width: '85%',
    margin: 'auto',
    flexDirection: 'column',
  },
};

const getInitCreateFlowFormState = () => ({
  campaignName: '',
  selectedGeoTargets: [],
  budget: '',
  campaignObjective: '',
});

const castNumberBudgetToString = (bid) => `$${formatStringToDollar(bid.toFixed(2))}`;

const castObjectiveToString = (campaignObjective) =>
  campaignObjective
    ? typeof campaignObjective === 'string'
      ? campaignObjective.toLowerCase()
      : campaignObjective.value
    : '';

const getCampaignSettings = (campaignSettings) => {
  const { name, objective, budget, selectedGeoTargets } = campaignSettings;
  return {
    campaignName: name || '',
    campaignObjective: strToTitleCase(objective),
    budget: budget !== undefined ? castNumberBudgetToString(budget) : '',
    selectedGeoTargets,
  };
};

const getFormState = (props) => {
  const { campaignSetup: createFlowCampaignSetupData, campaignSettings } = props;
  let state = {};
  if (campaignSettings) {
    state = getCampaignSettings(campaignSettings);
    return state;
  } else if (Object.keys(createFlowCampaignSetupData).length) {
    state = { ...createFlowCampaignSetupData };
  } else {
    state = getInitCreateFlowFormState();
  }
  return Object.assign({}, state, {
    campaignObjectiveDropdownIndicator: Icons.KEYBOARD_ARROW_DOWN,
    budgetInputBorderColor: css({ borderColor: 'none' }),
  });
};

class DiscoAdsCreateCampaign extends React.Component {
  state = getFormState(this.props);

  componentDidMount() {
    const { dateRange } = this.props.dateRangeInfo;
    this.props.retrieveDiscoAdsCampaigns(dateRange);
  }

  componentDidUpdate(prevProps) {
    const { campaignSettings, onCancel: onOpenCloseSettingsModal } = this.props;
    if (prevProps.campaignSettings !== campaignSettings) {
      onOpenCloseSettingsModal();
    }
  }

  _onSelectedGeoTargetsChange = (selected, { action }) => {
    const { selectedGeoTargets } = this.state;
    if (action === 'select-option') {
      let newSelectedGeoTargets;
      if (selected.length > 1) {
        const newSelection = [...selected].pop();
        newSelectedGeoTargets = [newSelection, ...selectedGeoTargets]; // move new selection to front of list
      } else {
        newSelectedGeoTargets = selected;
      }
      this.setState({
        selectedGeoTargets: newSelectedGeoTargets,
      });
    }
  };

  _onRemoveGeoTargetFromSelected = (geoTargetToRemove) => {
    const { selectedGeoTargets } = this.state;
    const newSelectedGeoTargets = selectedGeoTargets.filter((geoTarget) => geoTarget.id !== geoTargetToRemove.id);
    this.setState({ selectedGeoTargets: newSelectedGeoTargets });
  };

  _onRemoveAllGeoTargetsFromSelected = () => {
    this.setState({ selectedGeoTargets: [] });
  };

  _onCampaignObjectiveChange = (campaignObjective) => {
    this.setState({ campaignObjective });
  };

  _onBudgetInputChange = (e) => {
    const { value } = e.target;
    if (value === '$') {
      return;
    }
    if (value[1] === '$') {
      this.setState({ budget: '$' });
      return;
    }
    const budgetWithoutDollarSign = value.slice(1);
    const isBudgetValid = isBidValid(budgetWithoutDollarSign);
    const borderColor = `${isBudgetValid ? 'none' : theme.colors.error.main.concat(' !important')}`;
    this.setState({
      budget: `$${budgetWithoutDollarSign}`,
      budgetInputBorderColor: css({ borderColor }),
    });
  };

  _onBudgetInputBlur = (e) => {
    const { value } = e.target;
    if (value === '$') {
      this.setState({
        budget: '',
        budgetInputBorderColor: css({ borderColor: 'none' }),
      });
    }
  };

  _onCampaignNameInputChange = (e) => {
    const { value } = e.target;
    this.setState({
      campaignName: value,
    });
  };

  _onDropdownMenuOpen = (namespace) => {
    this.setState({
      [`${namespace}DropdownIndicator`]: Icons.KEYBOARD_ARROW_UP,
    });
  };

  _onDropdownMenuClose = (namespace) => {
    this.setState({
      [`${namespace}DropdownIndicator`]: Icons.KEYBOARD_ARROW_DOWN,
    });
  };

  _onBudgetInputFocus = () => {
    const { budget } = this.state;
    if (!budget.length) {
      this.setState({
        budget: '$',
      });
    }
  };

  _selectedGeoTargetsIsComplete = () => {
    return this.state.selectedGeoTargets?.length !== 0;
  };

  _campaignNameIsComplete = () => {
    return this.state.campaignName.length !== 0 && !this._isCampaignNameAndObjectiveDuplicated();
  };

  _budgetIsComplete = () => {
    const { budget } = this.state;
    const budgetWithoutDollarSign = budget.slice(1);
    return budgetWithoutDollarSign.length > 0 && isBidValid(budgetWithoutDollarSign);
  };

  _campaignObjectiveIsComplete = () => {
    if (this.props.campaignSettings) {
      return !!this.state.campaignObjective;
    }
    return !!this.state.campaignObjective.value;
  };

  allFieldsArecomplete = () => {
    return (
      this._campaignNameIsComplete() &&
      this._budgetIsComplete() &&
      this._campaignObjectiveIsComplete() &&
      this._selectedGeoTargetsIsComplete()
    );
  };

  _extractCampaignSetupDataFromState = () => {
    const { selectedGeoTargets, campaignObjective, campaignName, budget } = this.state;
    return {
      selectedGeoTargets,
      campaignObjective,
      campaignName,
      budget,
    };
  };

  _handleSave = () => {
    if (this.props.campaignSettings) {
      return this._onSaveCampaignSettings();
    }
    this._onSaveAndContinueClick();
  };

  _onSaveCampaignSettings = () => {
    const { campaignSettings, updateCurrentCampaign } = this.props;
    const { id } = campaignSettings;
    const { campaignName, selectedGeoTargets } = this.state;
    const payload = {
      id,
      // Only fields a user can currently update are "name" and "selectedGeoTargets"
      name: campaignName,
      selectedGeoTargets,
    };
    updateCurrentCampaign(payload);
  };

  _onSaveAndContinueClick = () => {
    const { onCompletionStatusChange, onNext, saveDiscoAdsCreatorStep } = this.props;
    const campaignSetupData = this._extractCampaignSetupDataFromState();
    saveDiscoAdsCreatorStep({
      stepKey: DA_CAMPAIGN_SETUP,
      payload: campaignSetupData,
    });
    onCompletionStatusChange(STEPPER_CARD_COMPLETE_STEP);
    onNext();
  };

  _getBudgetProps = () => {
    const { campaignSettings } = this.props;
    const { budgetInputBorderColor } = this.state;
    const { budgetInputField, disabledInput } = styles;
    if (campaignSettings) {
      return {
        inputFieldClassName: css(disabledInput, budgetInputField),
        inputIsDisabled: true,
        inputIsReadOnly: true,
      };
    }
    return {
      inputFieldClassName: css(budgetInputField, budgetInputBorderColor),
      onInputChange: this._onBudgetInputChange,
      onInputFocus: this._onBudgetInputFocus,
      onInputBlur: this._onBudgetInputBlur,
    };
  };

  _getCampaignObjectiveProps = () => {
    const { campaignSettings } = this.props;
    const { campaignObjective, campaignObjectiveDropdownIndicator } = this.state;
    if (campaignSettings) {
      return {
        inputFieldClassName: styles.disabledInput,
        inputValue: campaignObjective,
        inputIsDisabled: true,
        inputIsReadOnly: true,
      };
    }
    return {
      component: () => (
        <DiscoAdsCreatorCustomDropdown
          testid="dac-campaign-objective"
          placeholder="Select campaign objective"
          onMenuOpen={() => this._onDropdownMenuOpen('campaignObjective')}
          onMenuClose={() => this._onDropdownMenuClose('campaignObjective')}
          options={DA_CAMPAIGN_OBJECTIVE_OPTIONS}
          value={campaignObjective}
          onChange={this._onCampaignObjectiveChange}
          icon={campaignObjectiveDropdownIndicator}
          i13nLabel="Disco Ads Creator - Campaign Objective"
          customStyles={styles.campaignObjectiveDropdown}
        />
      ),
    };
  };

  _isCampaignNameAndObjectiveDuplicated = () => {
    const { campaignsNamesAndObjectives, campaignSettings } = this.props;
    const { campaignName, campaignObjective } = this.state;
    const initialCampaignName = campaignSettings?.name;

    const objective = castObjectiveToString(campaignObjective);
    const isCampaignNameAndObjectiveSet = campaignName && objective;
    const isCampaignNameModified = campaignName !== initialCampaignName;
    const isCampaignsListLoaded = !!campaignsNamesAndObjectives.length;

    const isDuplicated =
      isCampaignNameAndObjectiveSet &&
      isCampaignNameModified &&
      isCampaignsListLoaded &&
      campaignsNamesAndObjectives.reduce((acc, campaignNameAndObjective) => {
        return acc || (campaignName === campaignNameAndObjective.name && objective === campaignNameAndObjective.objective);
      }, false);

    return isDuplicated;
  };

  render() {
    const { appName, onCancel, campaignSettings } = this.props;

    const { campaignObjective, campaignName, budget, selectedGeoTargets } = this.state;

    const { campaign: tooltipCopy } = DA_CREATOR_TOOLTIP_COPY;
    const {
      campaign: {
        selectedApp: selectedAppCopy,
        geoTargets: geoTargetsCopy,
        name: campaignNameCopy,
        budget: budgetCopy,
        objective: objectiveCopy,
      },
    } = DA_CREATOR_FORM_SECTION_COPY;

    const campaignNameAndObjectiveDuplicated = this._isCampaignNameAndObjectiveDuplicated();
    const objectiveTitleCaseString = strToTitleCase(castObjectiveToString(campaignObjective));

    return (
      <DiscoAdsCreatorViewComponentWrapper
        saveAndContinueButtonIsDisabled={!this.allFieldsArecomplete()}
        onSaveClick={this._handleSave}
        onCancelClick={onCancel}
        saveButtonCopy={campaignSettings && AD_OBJECT_SETTINGS_SAVE_BTN_COPY}
        customStyles={campaignSettings ? campaignSettingsCustomFormWrapper : undefined}
      >
        <DiscoAdsCreatorFormSection
          title={selectedAppCopy.title}
          tooltipCopy={campaignSettings ? undefined : tooltipCopy.selectedApp}
          inputFieldClassName={styles.disabledInput}
          inputValue={appName}
          inputIsDisabled
          inputIsReadOnly
          testid="dac-selected-app"
        />
        <DiscoAdsCreatorFormSection
          title={geoTargetsCopy.title}
          subtitle={geoTargetsCopy.subtitle}
          component={() => (
            <GeoTargetMultiSelect
              selectedGeoTargets={selectedGeoTargets}
              onSelectedGeoTargetsChange={this._onSelectedGeoTargetsChange}
              onRemoveGeoTargetFromSelected={this._onRemoveGeoTargetFromSelected}
              onRemoveAllGeoTargetsFromSelected={this._onRemoveAllGeoTargetsFromSelected}
            />
          )}
        />
        <DiscoAdsCreatorFormSection
          title={campaignNameCopy.title}
          subtitle={campaignNameCopy.subtitle}
          onInputChange={this._onCampaignNameInputChange}
          inputPlaceholder="eg. Install Ad Demo"
          inputValue={campaignName}
          testid="dac-campaign-name"
        />
        {campaignNameAndObjectiveDuplicated && (
          <MessageBox
            type="error"
            noTitle
            text={[
              `A campaign with "${campaignName}" name and "${objectiveTitleCaseString}" objective already exists.`,
              'Please enter another name.',
            ]}
          />
        )}
        <DiscoAdsCreatorFormSection
          title={budgetCopy.title}
          subtitle={budgetCopy.subtitle}
          inputPlaceholder="eg. $100"
          inputValue={budget}
          testid="dac-campaign-budget"
          {...this._getBudgetProps()}
        />
        <DiscoAdsCreatorFormSection
          title={objectiveCopy.title}
          tooltipCopy={tooltipCopy.objective}
          {...this._getCampaignObjectiveProps()}
        />
      </DiscoAdsCreatorViewComponentWrapper>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    campaignSetup: getCampaignSetup(state),
    appName: getAppName(state),
    dateRangeInfo: getDateRangeInfo(state),
    campaignsNamesAndObjectives: getCampaignsNamesAndObjectives(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    saveDiscoAdsCreatorStep: ({ stepKey, payload }) => {
      dispatch(saveDiscoAdsCreatorStep({ stepKey, payload }));
    },
    updateCurrentCampaign: (payload) => {
      dispatch(updateCurrentCampaign(payload));
    },
    retrieveDiscoAdsCampaigns: (dateRange) => {
      dispatch(retrieveDiscoAdsCampaigns(dateRange));
    },
  };
};

const ConnectedDiscoAdsCreateCampaign = connect(mapStateToProps, mapDispatchToProps)(DiscoAdsCreateCampaign);

export default ConnectedDiscoAdsCreateCampaign;
